import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useStatusConfigs } from "@/hooks/use-status-configs";
import { StatusBadge } from "@/components/status-badge";
import type { YearWithProjects } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { NUMERIC_SEQUENCES, ALPHA_SEQUENCES, compareSequenceIds } from "@/lib/sequence-utils";
import { getUserColor } from '@/lib/user-color';

interface AddSequenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}

export function AddSequenceDialog({
  open,
  onOpenChange,
  projectId
}: AddSequenceDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [sequenceId, setSequenceId] = useState<string>("01");
  const [sequenceName, setSequenceName] = useState("");
  const [leadArtist, setLeadArtist] = useState<string>("none");
  const [selectedArtists, setSelectedArtists] = useState<string[]>([]);
  const [status, setStatus] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Sequences default to the blank/empty status (decoupled from the project
  // default flag, which is "New"). The catalog's blank entry has statusKey 'empty'.
  const SEQUENCE_DEFAULT_STATUS = 'empty';

  // Get status configurations for sequences (using project statuses)
  const { statusConfigs, isLoading: statusesLoading } = useStatusConfigs('project');

  // Fetch years data to find existing sequences
  const { data: years } = useQuery<YearWithProjects[]>({
    queryKey: ["/api/years"],
  });

  // Fetch artists for artist selection
  const { data: artists = [] } = useQuery({
    queryKey: ['/api/artists'],
  });

  // Find current project and calculate used sequence IDs
  const currentProject = years?.flatMap(y => y.projects).find(p => p.id === projectId);
  const usedIds = currentProject?.sequences?.map(s => s.sequenceNumber) || [];
  
  // Find next available numeric sequence
  const getNextAvailableId = (): string => {
    for (const id of NUMERIC_SEQUENCES) {
      if (!usedIds.includes(id)) return id;
    }
    return "01";
  };

  // Initialize form when dialog opens (only once per open)
  useEffect(() => {
    if (open && !isInitialized) {
      // Set next available ID as default suggestion
      setSequenceId(getNextAvailableId());
      setSequenceName("");
      setLeadArtist("none");
      setSelectedArtists([]);
      setStatus(SEQUENCE_DEFAULT_STATUS);
      setIsInitialized(true);
    } else if (!open) {
      // Reset initialization flag when dialog closes
      setIsInitialized(false);
    }
  }, [open, isInitialized, usedIds, statusesLoading, statusConfigs]);

  // Ensure default status is set (only if not already set)
  useEffect(() => {
    if (!status && isInitialized) {
      setStatus(SEQUENCE_DEFAULT_STATUS);
    }
  }, [status, isInitialized]);

  const createSequenceMutation = useMutation({
    mutationFn: async (sequenceData: any) => {
      const response = await apiRequest('POST', `/api/projects/${projectId}/sequences`, sequenceData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      toast({
        title: "Success",
        description: "Sequence created successfully",
      });
      onOpenChange(false);
      resetForm();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create sequence",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setSequenceId(getNextAvailableId());
    setSequenceName("");
    setLeadArtist("none");
    setSelectedArtists([]);
    setStatus(SEQUENCE_DEFAULT_STATUS);
    setIsInitialized(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (usedIds.includes(sequenceId)) {
      toast({
        title: "Error",
        description: "This sequence identifier is already used",
        variant: "destructive",
      });
      return;
    }

    createSequenceMutation.mutate({
      sequenceNumber: sequenceId,
      name: sequenceName,
      status,
      leadArtist: leadArtist === "none" ? "" : leadArtist,
      artists: selectedArtists
    });
  };

  const addArtist = (artistId: string) => {
    if (artistId && !selectedArtists.includes(artistId)) {
      setSelectedArtists([...selectedArtists, artistId]);
    }
  };

  const removeArtist = (artistId: string) => {
    setSelectedArtists(selectedArtists.filter(id => id !== artistId));
  };

  const getArtistById = (id: string) => {
    return (artists as any[]).find(artist => artist.id === id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] navy-card border-gray-600 overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-gray-100">Add New Sequence</DialogTitle>
          <DialogDescription className="text-gray-400">
            Add a new sequence to organize work phases within this project
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sequence Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Sequence Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sequenceNumber" className="text-gray-300">Sequence Identifier</Label>
                <Select
                  value={sequenceId}
                  onValueChange={setSequenceId}
                >
                  <SelectTrigger className="navy-bg border-gray-600 text-white" data-testid="select-sequence-number">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="navy-card border-gray-600 max-h-[300px]">
                    {/* Special */}
                    <div className="px-2 py-1 text-xs text-gray-500 font-semibold">Special</div>
                    <SelectItem
                      value="MST"
                      disabled={usedIds.includes("MST")}
                      className={usedIds.includes("MST") ? "text-gray-500" : "text-white"}
                    >
                      MST (Master) {usedIds.includes("MST") ? "(Used)" : ""}
                    </SelectItem>
                    
                    {/* Numeric */}
                    <div className="px-2 py-1 text-xs text-gray-500 font-semibold mt-1">Numeric (01-99)</div>
                    {NUMERIC_SEQUENCES.map((id) => (
                      <SelectItem
                        key={id}
                        value={id}
                        disabled={usedIds.includes(id)}
                        className={usedIds.includes(id) ? "text-gray-500" : "text-white"}
                      >
                        {id} {usedIds.includes(id) ? "(Used)" : ""}
                      </SelectItem>
                    ))}
                    
                    {/* Alpha */}
                    <div className="px-2 py-1 text-xs text-gray-500 font-semibold mt-1">Alphabetic (A-Z)</div>
                    {ALPHA_SEQUENCES.map((id) => (
                      <SelectItem
                        key={id}
                        value={id}
                        disabled={usedIds.includes(id)}
                        className={usedIds.includes(id) ? "text-gray-500" : "text-white"}
                      >
                        {id} {usedIds.includes(id) ? "(Used)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status" className="text-gray-300">Status</Label>
                <Select value={status} onValueChange={setStatus} disabled={statusesLoading}>
                  <SelectTrigger className="navy-bg border-gray-600 text-white" data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="navy-card border-gray-600">
                    {Array.isArray(statusConfigs) && statusConfigs.map((config) => (
                      <SelectItem key={config.id} value={config.statusKey} className="text-white">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: config.color }}
                          />
                          <span>{config.displayName}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="sequenceName" className="text-gray-300">Sequence Name (Optional)</Label>
              <Input
                id="sequenceName"
                value={sequenceName}
                onChange={(e) => setSequenceName(e.target.value)}
                placeholder="Enter sequence name..."
                className="navy-bg border-gray-600 text-white placeholder-gray-500"
                data-testid="input-sequence-name"
                autoComplete="off"
              />
            </div>
          </div>

          {/* Team Assignment Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Team Assignment</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="leadArtist" className="text-gray-300">Lead Artist</Label>
                <Select value={leadArtist} onValueChange={setLeadArtist}>
                  <SelectTrigger className="navy-bg border-gray-600 text-white">
                    <SelectValue placeholder="Select lead artist" />
                  </SelectTrigger>
                  <SelectContent className="navy-card border-gray-600">
                    <SelectItem value="none" className="text-white">No lead artist</SelectItem>
                    {(artists as any[]).map((artist) => (
                      <SelectItem key={artist.id} value={artist.shortCode} className="text-white">
                        {artist.firstName} {artist.lastName} ({artist.shortCode})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-300">Team Members</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedArtists.map((artistId) => {
                    const artist = getArtistById(artistId);
                    return artist ? (
                      <Badge
                        key={artistId}
                        variant="secondary"
                        className="flex items-center gap-1 text-white"
                        style={{ backgroundColor: getUserColor(artist.colorCode) }}
                      >
                        {artist.firstName} {artist.lastName} ({artist.id})
                        <X
                          className="w-3 h-3 cursor-pointer"
                          onClick={() => removeArtist(artistId)}
                        />
                      </Badge>
                    ) : null;
                  })}
                </div>
                
                <Select onValueChange={addArtist}>
                  <SelectTrigger className="navy-bg border-gray-600 text-white">
                    <SelectValue placeholder="Add team member" />
                  </SelectTrigger>
                  <SelectContent className="navy-card border-gray-600">
                    {(artists as any[])
                      .filter(artist => !selectedArtists.includes(artist.id))
                      .map((artist) => (
                      <SelectItem key={artist.id} value={artist.id} className="text-white">
                        {artist.firstName} {artist.lastName} ({artist.shortCode})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-6 border-t border-gray-600">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={createSequenceMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              {createSequenceMutation.isPending ? "Creating..." : "Create Sequence"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}